import React, { ReactNode, useEffect, useRef, useState } from "react";
import "../../assets/fa-icons/css/fontawesome.css";
import "../../assets/fa-icons/css/brands.css";
import "../../assets/fa-icons/css/sharp-regular.css";
import { cn } from "../utils/cn.ts";

export type ShareMethod = "native" | "facebook" | "twitter" | "linkedin";

const socialPlatforms: {
  name: Exclude<ShareMethod, "native">;
  icon: string;
  label: string;
  url: (url: string) => string;
}[] = [
  {
    name: "facebook",
    icon: "fa-facebook-f",
    label: "Share on Facebook",
    url: (url: string) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    name: "twitter",
    icon: "fa-x-twitter",
    label: "Share on X (formerly Twitter)",
    url: (url: string) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`,
  },
  {
    name: "linkedin",
    icon: "fa-linkedin-in",
    label: "Share on LinkedIn",
    url: (url: string) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  },
];

interface SharingProps {
  /** Absolute or site-relative share target. When absent, the component renders nothing. */
  url?: string;
  /**
   * Title offered to the native share sheet, defaulting to the property copy the only current
   * caller wants. Many share targets ignore it: Android maps it to the email/SMS subject, while
   * iOS and macOS build their preview card from the shared page's own og: tags instead.
   */
  shareTitle?: string;
  className?: string;
  iconClassName?: string;
  nativeShareLabel?: ReactNode;
  nativeShareAriaLabel?: string;
  onShare?: (method: ShareMethod) => void;
  expandable?: boolean;
  expandAriaLabel?: string;
  onExpandedChange: (isExpanded: boolean) => void;
  isExpanded: boolean;
}

/**
 * Renders the Web Share API button when available, falling back to per-platform share links.
 *
 * The fallback is rendered during SSR and on the first client render so markup matches during
 * hydration; the native-share button is swapped in afterwards if the browser supports it. With
 * `expandable`, the fallback starts as a single share button that reveals the platform links on click,
 * driven by the caller-owned `isExpanded`.
 */
export const Sharing: React.FC<SharingProps> = ({
  url,
  shareTitle = "I found this property on espc.com",
  className,
  iconClassName,
  nativeShareLabel,
  nativeShareAriaLabel = "Share",
  onShare,
  expandable = false,
  expandAriaLabel = "Share",
  onExpandedChange,
  isExpanded,
}) => {
  const [canNativeShare, setCanNativeShare] = useState<boolean | undefined>(undefined);
  const [origin, setOrigin] = useState("");
  const firstPlatformRef = useRef<HTMLAnchorElement | null>(null);

  useEffect(() => {
    setCanNativeShare(typeof navigator !== "undefined" && typeof navigator.share === "function");
    setOrigin(window.location.origin);
  }, []);

  useEffect(() => {
    if (canNativeShare !== false || !("fonts" in document)) return;
    void document.fonts.load("400 1em 'Font Awesome 6 Brands'");
  }, [canNativeShare]);

  useEffect(() => {
    if (!expandable || !isExpanded) return;
    firstPlatformRef.current?.focus();
  }, [expandable, isExpanded]);

  // Without a url there is nothing safe to share: an empty value resolves to the site root, which
  // would silently share the wrong page, so render no share control at all.
  if (!url) return null;

  // The share targets need an absolute URL, but callers pass a site-relative path (and during SSR
  // there is no origin to resolve against), so resolve on the client once the origin is known.
  const absoluteUrl = origin ? new URL(url, origin).toString() : url;

  const shareButton = (ariaLabel: string, onClick: () => void) => (
    <button className={className} onClick={onClick} aria-label={ariaLabel} type="button">
      <i aria-hidden="true" className={cn("fa-sharp fa-regular fa-share-nodes", iconClassName)} />
      {nativeShareLabel}
    </button>
  );

  if (canNativeShare) {
    return shareButton(nativeShareAriaLabel, () => {
      onShare?.("native");
      void navigator.share({ url: absoluteUrl, title: shareTitle }).catch(console.error);
    });
  }

  if (expandable && !isExpanded) {
    return shareButton(expandAriaLabel, () => onExpandedChange(true));
  }

  return (
    <>
      {socialPlatforms.map((platform, index) => (
        <a
          key={platform.name}
          ref={index === 0 ? firstPlatformRef : undefined}
          href={platform.url(absoluteUrl)}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          aria-label={platform.label}
          onClick={() => onShare?.(platform.name)}
        >
          <i className={cn("fa-brands", platform.icon, iconClassName)} aria-hidden="true" />
        </a>
      ))}
    </>
  );
};
